'use client'

import {Info} from 'lucide-react'
import {useSnackbar} from 'notistack'
import {useRef, useState} from 'react'
import {useLocation} from 'react-router-dom'
import {EventsPermissions, LprPermissions} from 'src/common/utils/PermissionsList'
import {Popover, PopoverContent, PopoverDescription, PopoverTitle, PopoverTrigger} from 'src/components/popover'
import {PortalViewSwitcherOnboarding} from 'src/components/portal-migration/portal-view-switcher-onboarding'
import {PortalViewSwitcherTrigger} from 'src/components/portal-migration/portal-view-switcher-trigger'
import {Switch} from 'src/components/switch'
import {useAuth} from 'src/contexts/AuthContext'
import {usePortalModulePrefs} from 'src/hooks/use-portal-module-prefs'
import {usePortalViewSwitcherOnboarding} from 'src/hooks/use-portal-view-switcher-onboarding'
import {cn} from 'src/layouts/main-layout/app-sidebar/utils/utils'
import {MIGRATED_PORTAL_MODULES, isPortalRouteHybridEnabled, isPostLoginDashboardPath} from 'src/lib/portal-migration'
import {applyPortalModulePrefNavigation} from 'src/lib/portal-migration/apply-portal-module-pref-navigation'

const BRIDGE_ERROR_MESSAGE = 'Não foi possível abrir o portal novo. Verifique a configuração ou tente novamente.'

export function PortalViewSwitcher() {
  const triggerRef = useRef<HTMLDivElement>(null)
  const spotlightMeasureRef = useRef<HTMLSpanElement>(null)
  const {pathname, search} = useLocation()
  const hybridEnabled = isPortalRouteHybridEnabled()
  const onboarding = usePortalViewSwitcherOnboarding(hybridEnabled && isPostLoginDashboardPath(pathname))
  const [open, setOpen] = useState(false)
  const [isNavigating, setIsNavigating] = useState(false)
  const {enqueueSnackbar} = useSnackbar()
  const {user} = useAuth()
  const {isModuleEnabled, setModuleEnabled, refreshPrefs} = usePortalModulePrefs()
  const userPermissions = user?.permissions ?? []

  const visibleModules = MIGRATED_PORTAL_MODULES.filter((module) => {
    if (module.legacyPath === '/events') {
      return EventsPermissions.every((permission) => userPermissions.includes(permission.name))
    }
    if (module.legacyPath === '/license-plate') {
      return LprPermissions.every((permission) => userPermissions.includes(permission.name))
    }
    return false
  })

  if (!hybridEnabled || visibleModules.length === 0) return null

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen)
    if (nextOpen) refreshPrefs()
  }

  const handleToggle = (moduleId: string, enabled: boolean) => {
    if (isNavigating) return

    const portalModule = visibleModules.find((item) => item.id === moduleId)
    if (!portalModule) return

    setModuleEnabled(moduleId, enabled)

    const outcome = applyPortalModulePrefNavigation({
      module: portalModule,
      enabled,
      pathname,
      search,
    })

    if (outcome.navigated) {
      setIsNavigating(true)
      setOpen(false)
      return
    }

    if (enabled && outcome.bridge && !outcome.bridge.ok) {
      setModuleEnabled(moduleId, false)
      enqueueSnackbar(BRIDGE_ERROR_MESSAGE, {variant: 'error'})
    }
  }

  return (
    <>
      <PortalViewSwitcherOnboarding measureRef={spotlightMeasureRef} open={onboarding.visible} onDismiss={onboarding.dismiss} />
      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <PortalViewSwitcherTrigger ref={triggerRef} spotlightMeasureRef={spotlightMeasureRef} className={cn(onboarding.visible && 'pointer-events-none')} />
        </PopoverTrigger>

        <PopoverContent align='end' sideOffset={8} data-slot='portal-view-switcher-content' className='w-80 rounded-xl border-[#EAECF0] p-0 shadow-lg'>
          <div className='flex flex-col gap-4 p-4'>
            <div className='flex flex-col gap-1'>
              <PopoverTitle className='text-sm font-semibold text-[#101828]'>Alternar visualização do portal</PopoverTitle>
              <PopoverDescription className='text-xs leading-5 text-[#475467]'>Escolha em quais módulos deseja utilizar a nova interface.</PopoverDescription>
            </div>

            <ul className='flex flex-col gap-3' role='list'>
              {visibleModules.map((portalModule) => (
                <li key={portalModule.id} className='flex items-center gap-3' data-slot='portal-view-switcher-module'>
                  <Switch
                    id={`portal-view-${portalModule.id}`}
                    checked={isModuleEnabled(portalModule.id)}
                    disabled={isNavigating}
                    onCheckedChange={(checked) => handleToggle(portalModule.id, checked)}
                    aria-label={`Usar novo visual em ${portalModule.label}`}
                  />
                  <label htmlFor={`portal-view-${portalModule.id}`} className='cursor-pointer text-sm font-medium text-[#344054]'>
                    {portalModule.label}
                  </label>
                </li>
              ))}
            </ul>

            <div className='flex gap-2 rounded-lg bg-[#EFF8FF] px-3 py-2'>
              <Info className='mt-0.5 size-4 shrink-0 text-[#53B1FD]' aria-hidden />
              <p className='text-xs leading-5 text-[#175CD3]'>Os módulos não selecionados continuarão na interface atual.</p>
            </div>

            <button
              type='button'
              data-slot='portal-view-switcher-close'
              disabled={isNavigating}
              onClick={() => setOpen(false)}
              className='h-9 w-full rounded-md bg-primary text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-50'>
              Fechar
            </button>
          </div>
        </PopoverContent>
      </Popover>
    </>
  )
}
